/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createOperationCall, OperationHandler } from 'src/core/services/api/createOperationCall';
import { httpService } from 'src/core/services/api/httpService';
import { ResourceListResults } from 'src/core/types/ResourceListTypes';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { NewTrainingJobSwagger, TrainingMode } from 'src/modules/modelTraining/types/trainingSwaggers';
import { EvaluationOptions } from 'src/modules/modelTraining/types/trainingTypes';

export const getCluTrainingService = createService(({ context, getEnvironment }) => {
    const { apiUrl, isProduction } = getEnvironment({ apiName: 'clu' });

    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    const getUrlBuilder = (projectName?: string) =>
        urlBuilder()
            .baseUrl(apiUrl)
            .project(projectName)
            .queryParam('api-version', LuisConstants.Clu.AuthoringApiVersion);

    const initiateTraining = async ({
        projectName,
        trainingModelName,
        trainingConfigVersion = 'latest',
        trainingMode = 'standard',
        evaluationOptions
    }: {
        projectName: string;
        trainingModelName: string;
        trainingMode: TrainingMode;
        trainingConfigVersion: string;
        evaluationOptions?: EvaluationOptions;
    }) => {
        const url = getUrlBuilder(projectName)
            .resource(':train')
            .build();

        const payload = {
            modelLabel: trainingModelName,
            trainingMode,
            trainingConfigVersion,
            evaluationOptions
        };

        const { headers: trainJobHeaders } = await httpService.post<void>(url, payload, apiOptions);

        return (<Record<string, string>>trainJobHeaders)[LuisConstants.OperationHeaders.LocationHeader];
    };

    const pollTraining = async ({
        statusUrl,
        jobId,
        projectName,
        operationHandler
    }: {
        statusUrl?: string;
        jobId?: string;
        projectName?: string;
        operationHandler: OperationHandler<NewTrainingJobSwagger>;
    }): Promise<void> => {
        if (!statusUrl) {
            statusUrl = getUrlBuilder(projectName)
                .resource('train')
                .resource('jobs', jobId)
                .build();
        }
        await createOperationCall(statusUrl, apiOptions, operationHandler);
    };

    const getTrainingJobs = async ({
        nextLink,
        projectName
    }: {
        nextLink?: string;
        projectName: string;
    }): Promise<ResourceListResults<NewTrainingJobSwagger>> => {
        const url =
            nextLink ||
            getUrlBuilder(projectName)
                .resource('train')
                .resource('jobs')
                .build();

        const { data } = await httpService.get<ResourceListResults<NewTrainingJobSwagger>>(url, apiOptions);

        return data;
    };

    const cancelTraining = async ({ projectName, jobId }: { projectName: string; jobId: string }): Promise<void> => {
        const url = getUrlBuilder(projectName)
            .resource('train')
            .resource('jobs', jobId)
            .resource(':cancel')
            .build();

        await httpService.post<void>(url, {}, apiOptions);
    };

    return { initiateTraining, pollTraining, getTrainingJobs, cancelTraining };
});
